import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { MessageSquare, Plus, ArrowUp } from "lucide-react";

/** Compact thread widget for book/author/publisher detail pages. */
export const RelatedDiscussions = ({ targetType, target }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });

  const { data, reload: load } = useFetch(target?.id ? `/threads?linked_target_type=${targetType}&linked_target_id=${target.id}&limit=5` : null);
  const items = data?.items || [];

  const submit = async () => {
    if (!user) { toast.error("Please log in"); return; }
    try {
      await api.post("/threads", {
        title: form.title, body: form.body,
        linked_target_type: targetType, linked_target_id: target.id,
      });
      setOpen(false); setForm({ title: "", body: "" });
      load();
      toast.success("Discussion started");
    } catch { toast.error("Failed"); }
  };

  return (
    <section className="space-y-4" data-testid="related-discussions">
      <div className="flex items-center justify-between">
        <h2 className="text-[1.6rem] sm:text-[2rem]">{t("discussions_title")}</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="start-discussion-btn"><Plus size={14} className="mr-1" aria-hidden="true" />{t("start_thread")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Discuss this {targetType}</DialogTitle></DialogHeader>
            <Input placeholder="Title" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="linked-thread-title" />
            <Textarea placeholder="What do you want to discuss?" rows={5} value={form.body} onChange={e=>setForm({...form,body:e.target.value})} data-testid="linked-thread-body" />
            <Button onClick={submit} data-testid="linked-thread-submit">Publish</Button>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 && <p className="text-muted-foreground">{t("no_discussions")}</p>}
      <div className="space-y-2">
        {items.map(t => (
          <Link key={t.id} to={`/discussions/${t.id}`} className="block bg-card border border-border rounded p-3 hover:border-primary/40 transition-colors" data-testid={`related-thread-${t.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center min-w-[2rem]">
                <ArrowUp size={12} className="text-primary" />
                <span className="text-xs">{t.upvote_count || 0}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm line-clamp-1">{t.title}</div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                  <span>{t.user_name}</span>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1"><MessageSquare size={10} />{t.reply_count || 0}</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};
